import { Button, Icon, List, ListItemPaper, Loader, Modal, ScrollContainer, SimpleCard, Stack, styled, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { useCallback, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { PanelSection } from "@tm/controls"
import { createSelector } from "reselect"
import { getRimCertificateAppType } from "../../../data/helpers"
import { MainState } from "../../main"
import { ValueItemsDefinition } from "../business"
import { fixtureItems, technicalDataFields } from "../business/helper"
import { StatusPanel } from "./statusPanel"
import { TireSizes } from "."
import { MountingPadsV2 } from "./mountingPadsV2"
import { RimDetailsArticle } from "../../../data/model"
import { Fixture } from "../../../data/repositories/wheels-loadRimFixtures/model"
import { RimCertificateAppType } from "../../../data/enums"

const StyledList = styled(List)({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    ".MuiPaper-elevation1": {
        paddingBlock: "5px",
    },
    maxHeight: "calc(100vh - 45em)",
    margin: "1px",
})

const StyledButton = styled(Button)({
    "&.MuiButton-sizeLarge": { padding: ".4em" },
    maxWidth: "fit-content",
})

const StyledPanelSection = styled(PanelSection)({
    width: "100% !important",
    height: "calc(100vh - 43em) !important",
    margin: "0 !important",
    boxShadow: "none !important",
    ".fancy-list__item": {
        padding: "0 0.5rem",
    },
})

const selector = createSelector(
    (s: MainState) => ({
        selectedRimDetailsArticle: s.wheelsDetails.selectedRimDetailsArticle,
        rimInfoTireRestrictionsResponse: s.wheelsDetails.rimInfoTireRestrictionsResponse,
        rimInfoRimRestrictionsResponse: s.wheelsDetails.rimInfoRimRestrictionsResponse,
        loading: s.wheelsDetails.rimDetailsArticlesResponse.loading,
        isTyresAvailable: s.navigation.isTyresAvailable,
        registrationNos: s.vehicleSelection,
        isConti: s.wheelsDetails.rimItem?.isConti,
        rimInfoTireSizesResponse: s.wheelsDetails.rimInfoTireSizesResponse,
        selectedTireSize: s.wheelsDetails.selectedTireSize,
    }),
    (x) => x
)

export function RimInformationV2() {
    const { translateText } = useLocalization()
    const {
        selectedRimDetailsArticle,
        rimInfoTireRestrictionsResponse,
        rimInfoRimRestrictionsResponse,
        loading,
        isTyresAvailable,
        registrationNos,
        isConti,
        rimInfoTireSizesResponse,
        selectedTireSize,
    } = useSelector(selector)

    const [isMountingPadsModalOpen, setIsMountingPadsModalOpen] = useState(false)
    const mountingPadsDisabled = useMemo(
        () =>
            !selectedTireSize ||
            loading ||
            rimInfoTireRestrictionsResponse.loading ||
            rimInfoRimRestrictionsResponse.loading ||
            (!rimInfoTireRestrictionsResponse?.data?.length && !rimInfoRimRestrictionsResponse?.data?.length),
        [
            selectedTireSize,
            loading,
            rimInfoRimRestrictionsResponse?.data?.length,
            rimInfoRimRestrictionsResponse.loading,
            rimInfoTireRestrictionsResponse?.data?.length,
            rimInfoTireRestrictionsResponse.loading,
        ]
    )

    const renderItem = (item: ValueItemsDefinition<keyof RimDetailsArticle>) => {
        return (
            <ListItemPaper sx={{ display: "flex" }}>
                <Typography sx={{ flexBasis: "50%" }}>{translateText(item.description)}</Typography>
                {item.id !== 8 && ((selectedRimDetailsArticle && <Typography>{selectedRimDetailsArticle[item.value]?.toString()}</Typography>) || "")}
                {item.id === 8 && <Icon name={selectedRimDetailsArticle?.[item.value] ? "snowflake" : "close"} />}
            </ListItemPaper>
        )
    }

    const renderAccessoryItem = (item: ValueItemsDefinition<keyof Fixture>) => {
        return (
            <ListItemPaper sx={{ display: "flex" }}>
                <Typography sx={{ flexBasis: "50%" }}>
                    {typeof item.description === "number" ? translateText(item.description) : item.description}
                </Typography>
                {(selectedRimDetailsArticle?.fixture && <Typography>{selectedRimDetailsArticle?.fixture[item?.value] || "-"}</Typography>) || "-"}
            </ListItemPaper>
        )
    }

    const handleCertificateUrl = useCallback((certificateUrl: string) => {
        certificateUrl && window.open(certificateUrl, "_blank")
    }, [])

    const renderCertificateReports = () => {
        const AbeCertificate = selectedRimDetailsArticle?.certificates?.find((certificate) => certificate.appType === RimCertificateAppType.ABE)
        const EceCertificate = selectedRimDetailsArticle?.certificates?.find((certificate) => certificate.appType === RimCertificateAppType.ECE)
        const TtgCertificate = selectedRimDetailsArticle?.certificates?.find((certificate) => certificate.appType === RimCertificateAppType.TTG)
        const TgaCertificate = selectedRimDetailsArticle?.certificates?.find((certificate) => certificate.appType === RimCertificateAppType.TGA)

        return (
            <Stack>
                <Typography variant="body3" sx={{ textTransform: "uppercase" }} position="absolute" top="-1px">
                    {translateText(1022)}
                </Typography>
                <Stack direction="row" gap={1}>
                    <StyledButton
                        disabled={!getRimCertificateAppType(selectedRimDetailsArticle, RimCertificateAppType.ABE) || !selectedTireSize}
                        variant="outlined"
                        startIcon={<Icon name="file-pdf" />}
                        onClick={() => handleCertificateUrl(AbeCertificate?.certificateUrl || "")}
                    >
                        {translateText(12737)}
                    </StyledButton>
                    <StyledButton
                        disabled={!getRimCertificateAppType(selectedRimDetailsArticle, RimCertificateAppType.ECE) || !selectedTireSize}
                        variant="outlined"
                        startIcon={<Icon name="file-pdf" />}
                        onClick={() => handleCertificateUrl(EceCertificate?.certificateUrl || "")}
                    >
                        {translateText(12736)}
                    </StyledButton>
                    <StyledButton
                        disabled={!getRimCertificateAppType(selectedRimDetailsArticle, RimCertificateAppType.TTG) || !selectedTireSize}
                        variant="outlined"
                        startIcon={<Icon name="file-pdf" />}
                        onClick={() => handleCertificateUrl(TtgCertificate?.certificateUrl || "")}
                    >
                        {translateText(14086)}
                    </StyledButton>
                    <StyledButton
                        disabled={!getRimCertificateAppType(selectedRimDetailsArticle, RimCertificateAppType.TGA) || !selectedTireSize}
                        variant="outlined"
                        startIcon={<Icon name="file-pdf" />}
                        onClick={() => handleCertificateUrl(TgaCertificate?.certificateUrl || "")}
                    >
                        {translateText(12738)}
                    </StyledButton>
                </Stack>
            </Stack>
        )
    }

    if (loading || rimInfoTireSizesResponse.loading) {
        return (
            <SimpleCard sxCard={{ padding: "0 0.75em", maxWidth: "100vw" }}>
                <StatusPanel>
                    <Loader />
                </StatusPanel>
            </SimpleCard>
        )
    }

    const renderTechnicalDataFields = () => {
        if (selectedRimDetailsArticle?.fixture) {
            return (
                <Stack direction="row" gap={1}>
                    <Stack width="50%">
                        <Typography variant="h4">{translateText(13940)}</Typography>
                        <ScrollContainer>
                            <StyledList>{technicalDataFields.map(renderItem)}</StyledList>
                        </ScrollContainer>
                    </Stack>
                    <Stack width="50%">
                        <Typography variant="h4">{translateText(13802)}</Typography>
                        <ScrollContainer>
                            <StyledList>{fixtureItems.map(renderAccessoryItem)}</StyledList>
                        </ScrollContainer>
                    </Stack>
                </Stack>
            )
        }

        return (
            <Stack>
                <Typography variant="h4">{translateText(13940)}</Typography>
                <StyledList sx={{ flexWrap: "wrap", width: "100%", columnGap: 1 }}>{technicalDataFields.map(renderItem)}</StyledList>
            </Stack>
        )
    }

    return (
        <SimpleCard sxCard={{ padding: "0 0.75em", maxWidth: "100vw" }}>
            <Stack direction="row" gap={3}>
                <Stack width="50%" gap={1}>
                    <Typography variant="h2">{translateText(941)}</Typography>
                    {renderTechnicalDataFields()}
                </Stack>

                <Stack width="50%">
                    <Stack direction="row" gap={1} justifyContent="end">
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setIsMountingPadsModalOpen(true)
                            }}
                            disabled={mountingPadsDisabled}
                        >
                            {translateText(942)}
                        </Button>
                        <Modal
                            sx={{ inset: "0em 0em 3em 30%" }}
                            open={isMountingPadsModalOpen}
                            onOutsideClick={() => setIsMountingPadsModalOpen(false)}
                            smallCloseButton
                        >
                            <MountingPadsV2 />
                        </Modal>

                        {!(registrationNos && isConti) && renderCertificateReports()}
                    </Stack>
                    <StyledPanelSection className="rim-information">
                        <Typography variant="h2">{translateText(13941)}</Typography>
                        <Typography variant="body1">{translateText(isTyresAvailable ? 14092 : 14091)}</Typography>
                        <TireSizes largeCheckbox colorCheckIcon tireSizeSelectionEnabled />
                    </StyledPanelSection>
                </Stack>
            </Stack>
        </SimpleCard>
    )
}
